import { drawRaster, makeCanvas, ctxOf, rasterWidth, rasterHeight, rasterScale } from '@/engine/canvas';
import { flipH, Painter, shade } from './pixel';
import { C } from './palette';
import type { Facing } from '@/engine/math';

export type HairStyle = 'short' | 'long' | 'bangs' | 'bun' | 'cap' | 'ribbon';
export interface CharLook {
  hair: keyof typeof C.hair;
  style: HairStyle;
  cloth: keyof typeof C.cloth;
  pants: keyof typeof C.pants;
  accent?: string;
  blush?: boolean;
}
/** Authored at native resolution: large, intentional clusters, never a shrunk HD face. */
export const SPR_W = 24;
export const SPR_H = 30;
export const WALK_FRAMES = 32;
export type Expression = 'neutral' | 'worried' | 'afraid' | 'focused' | 'tired' | 'relieved';
export const EXPRESSIONS: Expression[] = ['neutral', 'worried', 'afraid', 'focused', 'tired', 'relieved'];
export type Pose = 'idle' | 'blink' | 'walkA' | 'walkB' | 'sneak' | 'sneakA' | 'sneakB';
type Gait = 'walk' | 'run' | 'sneak';
export interface CharSprites {
  frames: Record<Facing, Record<Pose, HTMLCanvasElement>>;
  cycles: Record<Facing, Record<Gait, HTMLCanvasElement[]>>;
  portrait: HTMLCanvasElement;
  portraitSad: HTMLCanvasElement;
  portraits: Record<Expression, [HTMLCanvasElement, HTMLCanvasElement]>;
  frame(facing: Facing, gait: Gait, step: number, expression: Expression, blink: boolean, breath: number, strength?: number): HTMLCanvasElement;
}

const INK = '#292535';
const SKIN = '#ffdcad';
const SKIN_SHADE = '#dba078';
const BOOT = '#514153';
const SOLE = '#9a8790';

/** A short pixel limb. All endpoints and widths lie on the same 1px art grid. */
function limb(p: Painter, x: number, y: number, ex: number, ey: number, width: number, color: string) {
  const n = Math.max(Math.abs(ex - x), Math.abs(ey - y), 1);
  for (let i = 0; i <= n; i++) p.rect(Math.round(x + (ex - x) * i / n), Math.round(y + (ey - y) * i / n), width, width, color);
}

export function buildCharacter(look: CharLook): CharSprites {
  const hair = C.hair[look.hair];
  const hairHi = C.hair[(look.hair + 'Hi') as keyof typeof C.hair] ?? shade(hair, 0.25);
  const hairSh = shade(hair, -0.28);
  const cloth = C.cloth[look.cloth];
  const clothSh = C.cloth[(look.cloth + 'Sh') as keyof typeof C.cloth] ?? shade(cloth, -0.25);
  const clothHi = shade(cloth, 0.23);
  const accent = look.accent ?? C.brass;
  const pants = C.pants[look.pants];
  const long = look.style === 'long' || look.style === 'ribbon';
  const heads = new Map<string, HTMLCanvasElement>();

  function head(dir: 'down' | 'up' | 'left', expression: Expression, blink: boolean) {
    const key = `${dir}:${expression}:${blink}`;
    const cached = heads.get(key); if (cached) return cached;
    const p = new Painter(20, 20, 1), side = dir === 'left';
    // Stepped crown and asymmetrical tufts form the silhouette; no smooth oval contours.
    p.rect(6, 1, 7, 2, INK).rect(4, 3, 12, 2, INK).rect(2, 5, 16, 8, INK);
    p.rect(1, 8, 18, 3, INK).rect(3, 11, 14, 6, INK).rect(6, 16, 8, 2, INK);
    p.rect(6, 2, 7, 3, hair).rect(4, 4, 12, 5, hair).rect(3, 6, 14, 7, hairSh);
    p.rect(3, 6, 13, 5, hair).rect(2, 9, 16, 2, hairSh);
    // Two broad highlight clusters, not individual strands or a noisy gradient.
    p.rect(6, 3, 6, 2, hairHi).rect(4, 5, 4, 2, hairHi);
    if (long) {
      p.rect(2, 11, 4, 8, INK).rect(14, 11, 4, 8, INK);
      p.rect(3, 11, 3, 7, hairSh).rect(14, 11, 3, 7, hairSh);
      p.rect(3, 12, 2, 5, hair).rect(14, 12, 2, 5, hair);
    }
    if (dir === 'up') {
      p.rect(4, 10, 12, 5, hair).rect(5, 15, 10, 1, hairSh);
      p.rect(4, 11, 2, 3, hairHi).rect(13, 13, 3, 2, hairSh);
      p.hline(8, 17, 4, SKIN_SHADE);
      if (long) p.rect(5, 12, 10, 6, hairSh).rect(6, 12, 7, 5, hair);
    } else {
      // Flat, warm face with one shadow plane and tiny dark eyes.
      p.rect(4, 10, 12, 5, SKIN).rect(5, 15, 10, 1, SKIN).rect(6, 16, 8, 1, SKIN_SHADE);
      p.rect(3, 12, 2, 2, SKIN_SHADE).rect(15, 12, 2, 2, SKIN_SHADE);
      p.hline(5, 10, 10, SKIN_SHADE);
      if (side) {
        p.rect(13, 9, 4, 7, hairSh).rect(14, 10, 2, 4, hair);
        p.rect(3, 11, 2, 3, SKIN).px(2, 13, SKIN).rect(12, 12, 2, 2, SKIN_SHADE);
      }
      // Fringe is cut into a few recognisable, chunky locks.
      if (look.style === 'bangs') {
        p.rect(3, 8, 4, 4, hairSh).rect(7, 8, 4, 3, hairSh).rect(12, 8, 4, 4, hairSh);
        p.rect(3, 8, 3, 3, hair).rect(7, 8, 3, 2, hair).rect(12, 8, 3, 3, hair);
      } else {
        p.rect(3, 8, 5, 4, hairSh).rect(7, 8, 4, 2, hairSh).rect(14, 8, 3, 4, hairSh);
        p.rect(3, 8, 4, 3, hair).rect(7, 8, 3, 1, hair).rect(14, 8, 2, 3, hair);
      }
      const eyes = side ? [5] : [6, 12];
      for (const x of eyes) {
        if (blink || expression === 'tired') p.hline(x, 14, 2, INK);
        else if (expression === 'relieved') p.hline(x, 13, 2, INK).px(x + 1, 12, INK);
        else {
          p.rect(x, expression === 'focused' ? 13 : 12, 2, expression === 'focused' ? 2 : 3, INK);
          if (expression === 'afraid') p.px(x, 12, C.sheet);
        }
      }
      const mouth = side ? 5 : 9;
      if (expression === 'afraid') p.rect(mouth, 15, 2, 2, INK);
      if (expression === 'worried') p.hline(mouth, 15, 2, SKIN_SHADE).px(side ? 8 : 15, 11, '#c4e6eb');
      if (expression === 'relieved') p.hline(mouth, 15, 2, SKIN_SHADE);
      if (look.blush) p.px(5, 15, C.blush).px(14, 15, C.blush);
    }
    if (look.style === 'short' && look.cloth !== 'orange') {
      p.rect(12, 1, 3, 2, INK).rect(12, 2, 2, 2, hair).px(16, 4, INK);
    }
    if (look.style === 'bun') {
      p.rect(side ? 13 : 10, 0, 6, 4, INK).rect(side ? 14 : 11, 0, 4, 3, hair);
      p.hline(side ? 14 : 11, 0, 3, hairHi).hline(side ? 13 : 10, 4, 6, accent);
    }
    if (look.style === 'cap') {
      p.rect(5, 2, 10, 2, INK).rect(3, 4, 14, 5, INK);
      p.rect(5, 3, 10, 2, accent).rect(4, 5, 12, 3, accent).hline(6, 3, 6, clothHi);
      p.rect(side ? 0 : 2, 8, 17, 2, INK).hline(side ? 0 : 2, 8, 16, clothSh);
      if (dir !== 'up') p.rect(9, 5, 3, 2, C.sheet);
    }
    if (look.style === 'ribbon') {
      p.rect(14, 5, 5, 3, INK).rect(14, 5, 2, 2, accent).rect(17, 5, 2, 2, accent);
      p.px(16, 6, C.sheet).vline(17, 8, 3, accent);
    }
    // A single droplet is enough to convey fear, including when facing away.
    if (expression === 'afraid') p.px(18, 10, '#e2f3ed').rect(17, 11, 2, 2, '#8fc3d2');
    const result = p.done(); heads.set(key, result); return result;
  }

  const cache = new Map<string, HTMLCanvasElement>();
  function frame(facing: Facing, gait: Gait, step: number, expression: Expression, blink: boolean, breath: number, strength = 1) {
    // Quantise pose parameters, not the travel of the character through the world.
    const amount = Math.round(Math.max(0, Math.min(1, strength)) * 8) / 8;
    const key = `${facing}:${gait}:${step}:${expression}:${blink}:${breath}:${amount}`;
    const old = cache.get(key); if (old) return old;
    const p = new Painter(SPR_W, SPR_H, 1), side = facing === 'left' || facing === 'right';
    const dir = facing === 'right' ? 'left' : facing;
    const moving = step >= 0 && amount > 0;
    const phase = moving ? step / WALK_FRAMES : 0.25;
    const run = gait === 'run', sneak = gait === 'sneak';
    const crouch = sneak ? 1 : 0;
    const bob = moving ? -Math.round(Math.abs(Math.sin(phase * Math.PI * 2)) * (run ? 1.7 : sneak ? 0.65 : 1) * amount) : breath;
    const lean = side && run && moving ? -Math.round(amount) : 0;
    const shoulder = 18 + crouch + bob, hip = 24 + Math.round(crouch / 2) + bob;
    const sway = moving && !side ? Math.round(Math.sin(phase * Math.PI * 2) * amount * 0.65) : 0;

    // The supporting foot travels backward on the ground for half a cycle. The
    // returning foot follows an eased arc; it never lifts during its stance phase.
    function foot(n: number) {
      const u = (phase + n * 0.5) % 1;
      const swing = u >= 0.5;
      const t = swing ? (u - 0.5) * 2 : u * 2;
      const travel = moving ? (swing ? -Math.cos(t * Math.PI) : 1 - 2 * t) * amount : 0;
      const lift = moving && swing ? Math.sin(t * Math.PI) * (run ? 3 : sneak ? 1 : 2) * amount : 0;
      const x = side ? 10 + Math.round(travel * (run ? 4 : sneak ? 2 : 3)) : 7 + n * 7 + Math.round(travel * 0.7);
      const y = 26 + Math.round((side ? 0 : travel * 1.3) - lift);
      return { x, y, travel };
    }
    function leg(n: number) {
      const f = foot(n), x = side ? 10 : 7 + n * 7;
      limb(p, x, hip - 1, f.x, f.y, 3, INK);
      limb(p, x + 1, hip, f.x + 1, f.y, 2, n === 0 && side ? clothSh : pants);
      p.rect(f.x - (side ? 1 : 0), f.y + 1, 4, 2, INK);
      p.hline(f.x - (side ? 1 : 0), f.y + 1, 3, BOOT).hline(f.x, f.y + 2, 3, SOLE);
    }
    leg(0); leg(1);

    function arm(n: number) {
      const wave = moving ? Math.sin(phase * Math.PI * 2 + n * Math.PI) * amount : 0;
      const x = side ? (n === 0 ? 8 : 12) + lean : (n === 0 ? 4 : 17) + sway;
      const hx = x + (side ? Math.round(wave * (run ? 3 : 2)) : Math.round(wave * 0.8));
      const hy = shoulder + (run && moving ? 3 : sneak ? 3 : 5) + Math.round(wave * (run ? 2 : 1));
      limb(p, x, shoulder + 1, hx, hy, 3, INK);
      limb(p, x, shoulder + 1, hx, hy - 1, 2, n === 0 ? clothSh : cloth);
      p.rect(hx, hy, 2, 2, SKIN).px(hx + 1, hy + 1, SKIN_SHADE);
    }
    arm(0);
    const tx = (side ? 8 : 7) + lean + sway, tw = side ? 8 : 10;
    p.rect(tx, shoulder, tw, hip - shoulder + 2, INK);
    p.rect(tx + 1, shoulder, tw - 2, hip - shoulder + 1, cloth);
    p.hline(tx, hip + 1, tw, INK).hline(tx + 1, hip, tw - 2, clothSh);
    p.vline(tx + tw - 2, shoulder + 1, hip - shoulder, clothSh).hline(tx + 1, shoulder + 1, 3, clothHi);
    if (dir === 'up') {
      p.rect(tx + 2, shoulder + 2, tw - 4, 5, INK).rect(tx + 3, shoulder + 2, tw - 6, 4, C.woodPale);
      p.hline(tx + 2, shoulder + 2, tw - 4, C.woodPaleDk).px(tx + 4, shoulder + 4, C.brass);
    } else if (look.style === 'cap') {
      limb(p, tx + tw - 3, shoulder, tx + 2, hip, 1, C.woodPaleDk);
      p.rect(tx + 1, hip - 2, 3, 2, C.woodPale);
    } else {
      p.rect(tx + Math.floor(tw / 2) - 1, shoulder, 2, 3, C.sheet);
      p.px(tx + 2, shoulder, clothHi).px(tx + tw - 3, shoulder, clothHi);
      if (look.cloth === 'red') p.hline(tx + 2, hip - 1, tw - 4, clothSh);
      else if (look.cloth === 'blue') p.rect(tx + 1, hip - 2, 2, 2, clothSh).rect(tx + tw - 3, hip - 2, 2, 2, clothSh);
      else if (look.style === 'ribbon') p.hline(tx + 3, shoulder + 1, 4, accent).px(tx + 4, shoulder + 2, accent);
      else p.px(tx + tw - 3, shoulder + 3, C.brass);
    }
    if (look.cloth === 'orange') p.hline(tx + 1, shoulder + 3, tw - 2, C.sheet);
    arm(1);
    // Head lag is slight: the body leads while the large crown stays readable.
    const headSway = moving && !side ? Math.round(Math.sin((phase - 0.08) * Math.PI * 2) * amount * 0.6) : 0;
    p.draw(head(dir, expression, blink), 2 + lean + headSway, 2 + crouch + bob);
    let result = p.done(); if (facing === 'right') result = flipH(result);
    if (cache.size >= 384) cache.delete(cache.keys().next().value!);
    cache.set(key, result); return result;
  }

  const cycles = { down: {}, up: {}, left: {}, right: {} } as CharSprites['cycles'];
  const frames = { down: {}, up: {}, left: {}, right: {} } as CharSprites['frames'];
  for (const facing of ['down', 'up', 'left', 'right'] as const) {
    // Gameplay requests its current pose through frame(). Keep full sequences
    // available for previews without allocating thousands of unused canvases
    // before the player has even chosen a character.
    for (const gait of ['walk', 'run', 'sneak'] as const) {
      let sequence: HTMLCanvasElement[] | undefined;
      Object.defineProperty(cycles[facing], gait, {
        enumerable: true,
        get: () => sequence ??= Array.from({ length: WALK_FRAMES }, (_, n) => frame(facing, gait, n, 'neutral', false, 0)),
      });
    }
    frames[facing] = {
      idle: frame(facing, 'walk', -1, 'neutral', false, 0), blink: frame(facing, 'walk', -1, 'neutral', true, 0),
      walkA: frame(facing, 'walk', 0, 'neutral', false, 0), walkB: frame(facing, 'walk', 16, 'neutral', false, 0),
      sneak: frame(facing, 'sneak', -1, 'focused', false, 0),
      sneakA: frame(facing, 'sneak', 0, 'neutral', false, 0), sneakB: frame(facing, 'sneak', 16, 'neutral', false, 0),
    };
  }
  const portraits = {} as CharSprites['portraits'];
  for (const expression of EXPRESSIONS) {
    portraits[expression] = [false, true].map(blink => {
      // Same native pixel head as the world, enlarged only when displayed in the HUD.
      const p = new Painter(24, 24, 1);
      p.rect(6, 18, 12, 6, INK).rect(7, 18, 10, 6, cloth).rect(14, 19, 3, 5, clothSh);
      p.draw(head('down', expression, blink), 2, 1);
      return p.done();
    }) as [HTMLCanvasElement, HTMLCanvasElement];
  }
  return { frames, cycles, portrait: portraits.neutral[0], portraitSad: portraits.worried[0], portraits, frame };
}

export function characterFrame(sprites: CharSprites, facing: Facing, gait: Gait, moving: boolean, anim: number, time: number, expression: Expression = 'neutral'): HTMLCanvasElement {
  const blink = time % 4.7 > 4.55;
  const step = moving ? Math.floor(anim * (gait === 'walk' ? 5 : 16 / 3)) % WALK_FRAMES : -1;
  // Fear uses a one-pixel shiver; normal idle has a slow, restrained breath.
  const breath = moving ? 0 : expression === 'afraid' ? Math.round(Math.sin(time * 12) * 0.55) : (time % 3.8 > 3.45 ? 1 : 0);
  return sprites.frame(facing, gait, step, expression, blink, breath);
}
export function characterPortrait(sprites: CharSprites, expression: Expression, time: number): HTMLCanvasElement {
  return sprites.portraits[expression][time % 4.7 > 4.55 ? 1 : 0];
}
const rotations = new WeakMap<HTMLCanvasElement, Map<number, HTMLCanvasElement>>();
export function rotated(src: HTMLCanvasElement, quarterTurns: number): HTMLCanvasElement {
  const old = rotations.get(src)?.get(quarterTurns); if (old) return old;
  const w = rasterWidth(src), h = rasterHeight(src);
  const c = makeCanvas(quarterTurns % 2 ? h : w, quarterTurns % 2 ? w : h, rasterScale(src)), ctx = ctxOf(c);
  ctx.translate(rasterWidth(c) / 2, rasterHeight(c) / 2); ctx.rotate(Math.PI / 2 * quarterTurns); drawRaster(ctx, src, -w / 2, -h / 2);
  const variants = rotations.get(src) ?? new Map<number, HTMLCanvasElement>();
  variants.set(quarterTurns, c); rotations.set(src, variants); return c;
}
